import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Download, ShieldCheck, Award, ListChecks } from "lucide-react";
import { apiClient } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { Button } from "@/components/ui/button";

export default function SchemeDetail() {
    const { id } = useParams();
    const { t, lang } = useLang();
    const [scheme, setScheme] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        setLoading(true);
        apiClient
            .get(`/schemes/${id}`)
            .then((r) => setScheme(r.data))
            .catch(() => setError("not-found"))
            .finally(() => setLoading(false));
    }, [id]);

    if (loading) {
        return <div className="max-w-4xl mx-auto py-32 text-center text-gray-500">Loading…</div>;
    }
    if (error || !scheme) {
        return (
            <div className="max-w-4xl mx-auto py-32 px-6 text-center">
                <h2 className="font-serif text-3xl font-semibold text-[#1A365D] mb-4">
                    Scheme not found
                </h2>
                <Link to="/schemes">
                    <Button className="bg-[#1A365D]">Back to Schemes</Button>
                </Link>
            </div>
        );
    }

    return (
        <div data-testid="scheme-detail-page">
            <section className="bg-[#1A365D] text-white pt-14 pb-14 md:pt-20 md:pb-20">
                <div className="max-w-4xl mx-auto px-6 md:px-12">
                    <Link
                        to="/schemes"
                        className="inline-flex items-center gap-2 text-sm text-[#F5B76E] hover:text-white mb-6"
                        data-testid="scheme-back-link"
                    >
                        <ArrowLeft size={16} /> {t.schemes.back}
                    </Link>
                    <div className="small-caps text-[#F5B76E] mb-4">
                        {t.schemes.categories[scheme.category] || scheme.category}
                    </div>
                    <h1 className="font-serif font-bold text-3xl sm:text-4xl lg:text-5xl leading-tight mb-6">
                        {lang === "en" ? scheme.name_en : scheme.name_ta}
                    </h1>
                    <p className="text-gray-300 leading-relaxed max-w-3xl">
                        {lang === "en" ? scheme.short_desc_en : scheme.short_desc_ta}
                    </p>
                    <div className="mt-8 flex flex-wrap gap-4">
                        {scheme.pdf_url && (
                            <a
                                href={scheme.pdf_url}
                                target="_blank"
                                rel="noreferrer"
                                data-testid="scheme-detail-pdf"
                            >
                                <Button className="bg-[#DD6B20] hover:bg-[#C05621] text-white h-11">
                                    <Download size={16} className="mr-2" />
                                    {t.schemes.downloadForm}
                                </Button>
                            </a>
                        )}
                    </div>
                </div>
            </section>

            <section className="py-16 md:py-20 bg-white">
                <div className="max-w-4xl mx-auto px-6 md:px-12 space-y-12">
                    <Block
                        icon={<Award size={20} />}
                        title="Overview"
                        body={lang === "en" ? scheme.description_en : scheme.description_ta}
                    />
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                        <div className="bg-[#F7FAFC] p-8 rounded-lg border border-gray-200">
                            <div className="small-caps text-gray-500 mb-3">Subsidy</div>
                            <div className="font-serif text-2xl font-semibold text-[#1A365D]">
                                {scheme.subsidy}
                            </div>
                        </div>
                        <div className="bg-[#1A365D] text-white p-8 rounded-lg">
                            <div className="small-caps text-[#F5B76E] mb-3">
                                Administered by
                            </div>
                            <div className="font-serif text-xl font-semibold">
                                District Industries Centre
                            </div>
                            <div className="text-sm text-gray-300 mt-2">
                                Contact your nearest DIC office for guided assistance.
                            </div>
                        </div>
                    </div>
                    <Block
                        icon={<ShieldCheck size={20} />}
                        title={t.schemes.eligibility}
                        body={lang === "en" ? scheme.eligibility_en : scheme.eligibility_ta}
                    />
                    <Block
                        icon={<Award size={20} />}
                        title={t.schemes.benefits}
                        body={lang === "en" ? scheme.benefits_en : scheme.benefits_ta}
                    />
                    <Block
                        icon={<ListChecks size={20} />}
                        title={t.schemes.process}
                        body={
                            lang === "en"
                                ? scheme.application_process_en
                                : scheme.application_process_ta
                        }
                    />
                </div>
            </section>
        </div>
    );
}

function Block({ icon, title, body }) {
    return (
        <div>
            <div className="flex items-center gap-3 mb-4">
                <span className="w-10 h-10 rounded-lg bg-[#F7FAFC] text-[#DD6B20] flex items-center justify-center">
                    {icon}
                </span>
                <h2 className="font-serif text-2xl md:text-3xl font-semibold text-[#1A365D]">
                    {title}
                </h2>
            </div>
            <p className="text-base leading-relaxed text-gray-700 whitespace-pre-line">
                {body}
            </p>
        </div>
    );
}
